import { CellTemplates } from "../elements/grid";
import Moment from "moment";
import { buildUrl } from "../../utils";
import { FinancialFilters } from "../../filters";
import { FinanceTypeKeyValueList, FinanceTypes, PaymentMethodKeyValueList, PaymentMethods, } from "../../classes/Api/Payment";
const relationsToLoad = [
    'invoice',
    'invoice.payments',
    'invoice.payments.financeAgreement.financeCompany',
    'invoice.payments.provider',
];
const financeStatuses = [
    { value: 'waiting', label: 'Awaiting Decision' },
    { value: 'unsigned', label: 'Un-Signed' },
    { value: 'signed', label: 'Signed' },
    { value: 'paid', label: 'Paid Out' },
    { value: 'banked', label: 'Banked' },
];
const commissionPaymentMethods = PaymentMethodKeyValueList.filter((item) => {
    return [PaymentMethods.CHEQUE, PaymentMethods.BANK_TRANSFER].includes(item.value);
});
/**
 * Bryntum combos expect {value, text} pairs, the API constants use {value, label}.
 */
const toComboItems = (list) => {
    return list.map(i => ({ value: i.value, text: i.label }));
};
const labelFor = (list, value) => {
    const match = list.find(i => i.value === value);
    return match ? match.label : '';
};
/**
 * Derives the pseudo "status" field from the payment's boolean flags. This mirrors
 * app/Filters/Deal/FinanceStatusFilter.php which performs the same mapping server side.
 */
const statusFromPayment = (payment) => {
    if (payment.is_posted && payment.is_approved && payment.is_banked) {
        return 'banked';
    }
    else if (payment.is_posted && payment.is_approved) {
        return 'paid';
    }
    else if (payment.is_approved && payment.is_signed) {
        return 'signed';
    }
    else if (payment.is_approved) {
        return 'unsigned';
    }
    return 'waiting';
};
/**
 * Expands a status selection back out into the boolean flags the API expects,
 * returning a set of dot delimited paths suitable for Model.set().
 */
const statusChangeSet = (status) => {
    const flags = {
        'finance_payment.status': status,
        'finance_payment.is_banked': false,
        'finance_payment.is_signed': false,
        'finance_payment.is_posted': false,
        'finance_payment.is_approved': false,
        'finance_payment.paid_at': null,
        'finance_payment.finance_agreement.commission_paid_at': null,
        'finance_payment.finance_agreement.commission_payment_method': null,
    };
    if ('unsigned' === status) {
        flags['finance_payment.is_approved'] = true;
    }
    else if ('signed' === status) {
        flags['finance_payment.is_signed'] = true;
        flags['finance_payment.is_approved'] = true;
    }
    else if ('paid' === status) {
        flags['finance_payment.is_signed'] = true;
        flags['finance_payment.is_posted'] = true;
        flags['finance_payment.is_approved'] = true;
    }
    else if ('banked' === status) {
        flags['finance_payment.is_banked'] = true;
        flags['finance_payment.is_signed'] = true;
        flags['finance_payment.is_posted'] = true;
        flags['finance_payment.is_approved'] = true;
        flags['finance_payment.paid_at'] = Moment().startOf('day').toDate();
    }
    return flags;
};
const getFinancePayment = (invoice) => {
    for (let i in invoice.payments) {
        if (PaymentMethods.FINANCE === invoice.payments[i].method) {
            return invoice.payments[i];
        }
    }
};
const toDate = (value) => value ? Moment(value).toDate() : null;
/**
 * Flattens the finance payment onto the deal and normalises the date fields into
 * Date instances so Bryntum's date editors can round trip them.
 */
const rowProcessor = (deal) => {
    const payment = getFinancePayment(deal.invoice);
    if (!payment) {
        return deal;
    }
    payment.status = statusFromPayment(payment);
    payment.paid_at = toDate(payment.paid_at);
    if (payment.finance_agreement) {
        payment.finance_agreement.commission_paid_at = toDate(payment.finance_agreement.commission_paid_at);
    }
    deal.finance_payment = payment;
    deal.is_dirty = deal.is_dirty === true;
    deal.is_busy = deal.is_busy === true;
    return deal;
};
/**
 * Cells that are only editable once the agreement reaches a given state. Consulted
 * from the grid's beforecelleditstart listener and used to grey out the cell.
 */
const isBanked = (record) => !!record.finance_payment?.is_banked;
const editableWhen = {
    balloon: (record) => record.finance_payment?.finance_type === FinanceTypes.PCP,
    paidAt: isBanked,
    commissionMethod: isBanked,
    commissionPaidAt: (record) => isBanked(record)
        && !!record.finance_payment?.finance_agreement?.commission_payment_method,
};
const disabledCell = '<span style="color: #ccc;">&mdash;</span>';
/**
 * Empty editable cells keep the affordance the old inline inputs had, showing the
 * same kind of placeholder rather than rendering as blank.
 */
const placeholderCell = (text) => `<span style="color: #b3b3b3;">${text}</span>`;
const CURRENCY_PLACEHOLDER = '0.00';
const SELECT_PLACEHOLDER = 'Select...';
const editableCurrency = (value) => {
    return value > 0 ? FinancialFilters.currency(value, 2) : placeholderCell(CURRENCY_PLACEHOLDER);
};
const editableChoice = (list, value) => {
    const label = labelFor(list, value);
    return label.length > 0 ? label : placeholderCell(SELECT_PLACEHOLDER);
};
const editableDate = (value) => {
    return value ? Moment(value).format('D/M/YYYY') : placeholderCell('Select date...');
};
/**
 * Reproduces the markup the old list rendered for this cell verbatim, inline styles
 * and all, so the customer/vehicle block reads exactly as it did before.
 */
const customerVehicleTemplate = {
    type: 'template',
    template: (data) => {
        const deal = data.record.data;
        const contact = deal.invoice?.contact;
        let html = CellTemplates.xss `<div><div style="font-size: 1.1rem;"><a href="/contacts/${deal.invoice?.contact_id ?? ''}">${contact?.primary_display_as ?? ''}</a></div>`;
        if (deal.instance) {
            const vehicleUrl = buildUrl('vehicles/%s/overview', [deal.instance.id]);
            html += '<div style="font-size: .8rem">';
            html += CellTemplates.xss `<a href="${vehicleUrl}" class="registration" style="display: inline-block; margin-top: 5px; font-size: .7em; width: 60px; margin-right: 5px; padding: .45em 0; color: #42526e; font-weight: 500;">${deal.instance.vehicle?.registration?.formatted_vrm ?? ''}</a>`;
            html += CellTemplates.xss `<a href="${vehicleUrl}" style="vertical-align: middle; display: inline-block; max-width: 57%;">${deal.instance.title ?? ''}</a>`;
            html += '</div></div>';
        }
        return html;
    }
};
const funderTemplate = {
    type: 'template',
    template: (data) => {
        const payment = data.record.data.finance_payment;
        let html = CellTemplates.xss `<div><div>${payment?.provider?.primary_display_as ?? '-'}</div>`;
        if (payment?.reference) {
            html += CellTemplates.xss `<div class="subtle truncate" style="max-width: 100%;">${payment.reference}</div>`;
        }
        html += '</div>';
        return html;
    }
};
/**
 * Columns default to htmlEncode: true, which would escape the markup our renderers
 * emit for placeholders and disabled cells. Template columns already opt out, plain
 * renderer columns have to be told. Vue columns return an object, not markup, so they
 * are left alone.
 */
const allowHtmlFromRenderers = (columns) => {
    return columns.map(column => {
        if (column.children) {
            return { ...column, children: allowHtmlFromRenderers(column.children) };
        }
        return column.renderer && !column.vue ? { ...column, htmlEncode: false } : column;
    });
};
const useGridConfig = (callbacks) => {
    /**
     * finishcelledit only fires once the editor is committed, so a typed value would not
     * enable the save button until the cell lost focus. Listening to the editor field's
     * input event instead marks the row dirty on the first keystroke. Only ever one
     * listener is attached, since the editor widget is reused across cells.
     */
    let detachDirtyListener;
    const releaseDirtyListener = () => {
        detachDirtyListener?.();
        detachDirtyListener = undefined;
    };
    const markDirtyWhileEditing = (editorContext) => {
        releaseDirtyListener();
        const field = editorContext.editor?.inputField;
        if (!field || !editorContext.record) {
            return;
        }
        detachDirtyListener = field.ion({
            input: () => editorContext.record.set('is_dirty', true)
        });
    };
    const columns = [
        {
            id: 'deal',
            text: 'Customer',
            locked: true,
            children: [
                {
                    text: 'Name / Vehicle',
                    field: 'invoice.contact.primary_display_as',
                    width: 320,
                    editor: false,
                    ...customerVehicleTemplate,
                    sum: 'count',
                    summaryRenderer: ({ sum }) => `${sum} agreements`,
                },
                {
                    text: 'Deal #',
                    field: 'id',
                    width: 90,
                    editor: false,
                    type: 'template',
                    template: (data) => CellTemplates.xss `<a href="/deals/${data.value}/edit">#${data.value}</a>`,
                }
            ]
        },
        {
            id: 'agreement',
            text: 'Agreement',
            children: [
                {
                    text: 'Funder',
                    field: 'finance_payment.provider.primary_display_as',
                    width: 200,
                    editor: false,
                    ...funderTemplate,
                },
                {
                    text: '£\'s Funded',
                    field: 'finance_payment.finance_agreement.amount_funded',
                    width: 130,
                    editor: false,
                    sum: true,
                    ...CellTemplates.summaryRoundedCurrencyWithPence,
                    ...CellTemplates.roundedCurrencyWithPence,
                },
                {
                    id: 'contra',
                    text: '£\'s Contra',
                    field: 'finance_payment.finance_agreement.total_contra_amount',
                    width: 140,
                    editor: false,
                    vue: true,
                    sum: true,
                    ...CellTemplates.summaryRoundedCurrencyWithPence,
                    renderer({ record }) {
                        const agreement = record.data.finance_payment?.finance_agreement;
                        return {
                            is: 'FinanceContraCell',
                            value: record,
                            amount: agreement?.total_contra_amount,
                            agreementId: agreement?.id,
                            callbacks,
                        };
                    },
                },
                {
                    text: '£\'s Payout',
                    field: 'finance_payment.finance_agreement.payout_amount',
                    width: 130,
                    editor: false,
                    sum: true,
                    ...CellTemplates.summaryRoundedCurrencyWithPence,
                    ...CellTemplates.roundedCurrencyWithPence,
                }
            ]
        },
        {
            id: 'finance',
            text: 'Finance',
            children: [
                {
                    text: 'Type',
                    field: 'finance_payment.finance_type',
                    width: 100,
                    editor: {
                        type: 'combo',
                        editable: false,
                        placeholder: SELECT_PLACEHOLDER,
                        items: toComboItems(FinanceTypeKeyValueList),
                    },
                    renderer: ({ value }) => editableChoice(FinanceTypeKeyValueList, value),
                },
                {
                    text: 'Term',
                    field: 'finance_payment.payment_term',
                    width: 90,
                    editor: { type: 'number', min: 0, step: 1, placeholder: 'e.g. 60' },
                    renderer: ({ value }) => value > 0 ? value : placeholderCell('e.g. 60'),
                },
                {
                    text: '£\'s P/M',
                    field: 'finance_payment.finance_monthly_payment',
                    width: 120,
                    editor: { type: 'number', min: 0, step: 0.01, placeholder: CURRENCY_PLACEHOLDER },
                    renderer: ({ value }) => editableCurrency(value),
                },
                {
                    id: 'balloon',
                    text: '£\'s Balloon',
                    field: 'finance_payment.finance_balloon_payment',
                    width: 120,
                    editor: { type: 'number', min: 0, step: 0.01, placeholder: CURRENCY_PLACEHOLDER },
                    editableWhen: editableWhen.balloon,
                    renderer: ({ value, record }) => {
                        if (!editableWhen.balloon(record.data)) {
                            return disabledCell;
                        }
                        return editableCurrency(value);
                    },
                },
                {
                    id: 'status',
                    text: 'Status',
                    field: 'finance_payment.status',
                    width: 160,
                    editor: {
                        type: 'combo',
                        editable: false,
                        placeholder: SELECT_PLACEHOLDER,
                        items: toComboItems(financeStatuses),
                    },
                    renderer: ({ value }) => editableChoice(financeStatuses, value),
                },
                {
                    id: 'paid_at',
                    text: 'Payment Date',
                    field: 'finance_payment.paid_at',
                    width: 140,
                    type: 'date',
                    format: 'DD/MM/YYYY',
                    editableWhen: editableWhen.paidAt,
                    renderer: ({ value, record }) => {
                        if (!editableWhen.paidAt(record.data)) {
                            return disabledCell;
                        }
                        return editableDate(value);
                    },
                }
            ]
        },
        {
            id: 'commission',
            text: 'Commission',
            children: [
                {
                    text: '£\'s Comm',
                    field: 'finance_payment.finance_agreement.commission_amount',
                    width: 120,
                    editor: { type: 'number', min: 0, step: 0.01, placeholder: CURRENCY_PLACEHOLDER },
                    sum: true,
                    ...CellTemplates.summaryRoundedCurrencyWithPence,
                    renderer: ({ value }) => editableCurrency(value),
                },
                {
                    id: 'commission_payment_method',
                    text: 'Comm Pmt. Method',
                    field: 'finance_payment.finance_agreement.commission_payment_method',
                    width: 170,
                    editor: {
                        type: 'combo',
                        editable: false,
                        placeholder: SELECT_PLACEHOLDER,
                        items: toComboItems(commissionPaymentMethods),
                    },
                    editableWhen: editableWhen.commissionMethod,
                    renderer: ({ value, record }) => {
                        if (!editableWhen.commissionMethod(record.data)) {
                            return disabledCell;
                        }
                        return editableChoice(commissionPaymentMethods, value);
                    },
                },
                {
                    id: 'commission_paid_at',
                    text: 'Comm Paid Date',
                    field: 'finance_payment.finance_agreement.commission_paid_at',
                    width: 150,
                    type: 'date',
                    format: 'DD/MM/YYYY',
                    editableWhen: editableWhen.commissionPaidAt,
                    renderer: ({ value, record }) => {
                        if (!editableWhen.commissionPaidAt(record.data)) {
                            return disabledCell;
                        }
                        return editableDate(value);
                    },
                }
            ]
        },
        {
            id: 'actions',
            text: '',
            region: 'right',
            children: [
                {
                    id: 'save',
                    text: '',
                    field: 'is_dirty',
                    width: 60,
                    align: 'center',
                    editor: false,
                    sortable: false,
                    filterable: false,
                    renderer({ record }) {
                        if (record.data.is_busy) {
                            return '<i class="material-icons finance-grid-saving">sync</i>';
                        }
                        const dirty = record.data.is_dirty === true;
                        return `<i class="material-icons finance-grid-save${dirty ? ' is-dirty' : ''}">save</i>`;
                    },
                    customEvents: {
                        click: (e) => {
                            const row = e.record.data;
                            if (row.is_dirty && !row.is_busy) {
                                callbacks.save(e.record);
                            }
                        }
                    },
                }
            ]
        }
    ];
    return {
        columns: allowHtmlFromRenderers(columns),
        rowHeight: 60,
        /**
         * The wrapper turns Bryntum's changeset diffing on by default, which only pays
         * off when the same rows are reloaded. Here every load is a different page or a
         * different filter, so the diff has nothing to reuse, and it is not safe on this
         * data: a page that carries the same deal twice leaves two records sharing an id,
         * and the next load's diff then resolves both to one index and removes it twice,
         * throwing part way through the changeset. The grid suspends its refresh for the
         * duration of a changeset, so a throw inside one leaves it suspended for good and
         * the grid silently stops redrawing. Loading each page outright avoids all of it.
         */
        store: { syncDataOnLoad: false },
        // Bound to the DataView store's paging by usePagingBridge(), see FinanceList.
        bbar: { type: 'pagingtoolbar' },
        selectionMode: {
            rows: true,
            multiSelect: true,
            // Cells open their editor on a single click, so rows must only be
            // selectable via the checkbox or every edit would also select a row.
            checkboxOnly: true,
            showCheckAll: true,
            deselectFilteredOutRecords: true,
        },
        features: {
            summary: true,
            filter: false,
            // Right click on a cell, and the column header dropdown.
            cellMenu: false,
            headerMenu: false,
            cellEdit: {
                // Defaults to celldblclick, the old list edited straight into its inputs.
                triggerEvent: 'cellclick',
            },
        },
        listeners: {
            /**
             * The toolbar has to be pointed at the paging facade before any data reaches
             * the store. Left on the plain store it has no paging API, so the first store
             * change throws out of PagingToolbar.onStoreChange(), which aborts the store's
             * changeset before the grid's endApplyChangeset handler runs and leaves the
             * grid's refresh suspended for good, i.e. never redrawing again. Paint happens
             * while the grid is being constructed, before the data prop can push anything
             * into the store.
             */
            paint: ({ source, firstPaint }) => {
                if (firstPaint) {
                    callbacks.attachPaging(source);
                }
            },
            /**
             * Registered here rather than on the Grid wrapper's selection-change event:
             * that listener is attached in onMounted, which runs before the async
             * BryntumGrid chunk has resolved, so it never binds.
             */
            selectionchange: ({ selection }) => {
                callbacks.selectionChanged(selection ?? []);
            },
            cellclick: (e) => {
                if (e.column.data.customEvents?.click) {
                    e.column.data.customEvents.click(e);
                }
            },
            /**
             * Veto edits on cells that are only editable once the agreement
             * reaches the relevant state.
             */
            beforecelleditstart: ({ editorContext }) => {
                const guard = editorContext.column.data.editableWhen;
                return guard ? guard(editorContext.record.data) : true;
            },
            startcelledit: ({ editorContext }) => {
                markDirtyWhileEditing(editorContext);
            },
            cancelcelledit: () => releaseDirtyListener(),
            /**
             * Selecting a status rewrites the underlying boolean flags, everything
             * else just marks the row dirty ready for the save button.
             */
            finishcelledit: ({ editorContext }) => {
                const { column, record, value } = editorContext;
                releaseDirtyListener();
                if (column.data.id === 'status') {
                    record.set(statusChangeSet(value));
                }
                record.set('is_dirty', true);
            },
        },
    };
};
/**
 * Applies the "Pay N Balances" bulk action to a row, before it is persisted down the
 * same path as the per row save button. Reuses the status expansion the inline editor
 * uses, so a bulk change and a single cell edit write exactly the same fields.
 */
const applyBulkStatus = (record, status, date) => {
    const changes = statusChangeSet(status);
    // statusChangeSet() clears paid_at for every status but banked, where it defaults
    // to today. In bulk the user picks that date themselves.
    if (status === 'banked') {
        changes['finance_payment.paid_at'] = date;
    }
    changes.is_dirty = true;
    record.set(changes);
};
/**
 * Applies the bulk commission update to a row. Kept separate from the status change so
 * the two halves of the update can be run independently, or together on the same row,
 * in which case this must run last: statusChangeSet() clears the commission fields.
 *
 * The API rejects a method without a date and a date without a method, so the modal
 * asks for both and they are always written as a pair.
 */
const applyBulkCommission = (record, method, date) => {
    record.set({
        'finance_payment.finance_agreement.commission_payment_method': method,
        'finance_payment.finance_agreement.commission_paid_at': date,
        is_dirty: true,
    });
};
/**
 * Identifies a row in the bulk update's error list.
 */
const rowLabel = (record) => {
    const deal = record.data;
    return `#${deal.id} ${deal.invoice?.contact?.primary_display_as ?? ''}`.trim();
};
/**
 * Bryntum's PagingToolbar drives an AjaxStore over HTTP: loadPage() issues a request,
 * and plain stores have no paging at all. Moving this grid onto an AjaxStore would mean
 * giving up the DataView store's filters, saved views and URL syncing, so instead the
 * toolbar is pointed at a facade that reads its paging state from Vuex and pages through
 * it. Row events are forwarded from the real grid store, so the toolbar still refreshes
 * itself whenever the data changes.
 */
const usePagingBridge = (grid, vuex, reload) => {
    const toolbar = grid?.bbar;
    const store = grid?.store;
    if (!toolbar || !store) {
        return false;
    }
    const paging = () => vuex.state.dataView;
    const facade = {
        ion: (config) => store.ion(config),
        get count() { return store.count; },
        get isLoading() { return paging().isBusy; },
        get pageSize() { return Number(paging().perPage); },
        get currentPage() { return Number(paging().currentPage); },
        get allCount() { return paging().resultSet?.total ?? 0; },
        get lastPage() { return Math.max(1, Math.ceil(facade.allCount / facade.pageSize)); },
        loadPage(page) {
            vuex.commit('dataView/setPage', Math.min(Math.max(1, page), facade.lastPage));
            return reload();
        },
        nextPage: () => facade.loadPage(facade.currentPage + 1),
        previousPage: () => facade.loadPage(facade.currentPage - 1),
    };
    toolbar.store = facade;
    // The first page may already have loaded against the real store before we swapped.
    toolbar.onStoreChange();
    return true;
};
export { useGridConfig, usePagingBridge, applyBulkStatus, applyBulkCommission, isBanked, rowLabel, relationsToLoad, rowProcessor, statusFromPayment, getFinancePayment, financeStatuses, commissionPaymentMethods, };
